import { useParams, useNavigate } from 'react-router-dom';
import { mockGuidelines } from '../../mocks/guidelines';
import { mockAppraisalGuidelines } from '../../mocks/appraisal-guidelines';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import { useEffect, useState } from 'react';

// Helper function for agency colors
function getAgencyColor(source: string) {
  switch (source) {
    case 'Fannie Mae':
      return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
    case 'Freddie Mac':
      return 'bg-violet-500/20 text-violet-400 border-violet-500/30';
    case 'HUD':
      return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    case 'VA':
      return 'bg-rose-500/20 text-rose-400 border-rose-500/30';
    default:
      return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  }
}

export default function GuidelinePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isPremiumMember, setIsPremiumMember] = useState(false);

  // Combine both guideline datasets
  const allGuidelines = [...mockGuidelines, ...mockAppraisalGuidelines];
  
  // Find guideline - convert both to strings for comparison
  const guideline = allGuidelines.find(g => String(g.id) === String(id));

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Check if user has premium membership
    const premiumStatus = localStorage.getItem('premium_member');
    setIsPremiumMember(premiumStatus === 'true');
  }, [id]);

  // If guideline not found, show error
  if (!guideline) {
    return (
      <div className="min-h-screen bg-slate-900">
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="w-24 h-24 flex items-center justify-center mx-auto mb-6 bg-slate-800 rounded-full">
              <i className="ri-file-text-line text-3xl text-slate-500"></i>
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">Guideline Not Found</h1>
            <p className="text-slate-400 mb-8">The guideline you're looking for doesn't exist or has been removed.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-500 font-medium cursor-pointer whitespace-nowrap transition-colors"
            >
              Back to Guidelines
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // If not a premium member, show paywall
  if (!isPremiumMember) {
    return (
      <div className="min-h-screen bg-slate-900">
        <Header />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back Button */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-slate-400 hover:text-emerald-400 mb-8 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Guidelines
          </button>

          {/* Preview Header */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-8 mb-8">
            <div className="flex flex-col sm:flex-row justify-between items-start mb-6 gap-4">
              <span className={`px-4 py-2 rounded-full text-sm font-medium border ${getAgencyColor(guideline.source)}`}>
                {guideline.source}
              </span>
              <div className="flex items-center space-x-4 text-sm text-slate-400">
                <span className="flex items-center">
                  <i className="ri-folder-line mr-1"></i>
                  {guideline.category}
                </span>
                <span>
                  Updated {new Date(guideline.lastUpdated).toLocaleDateString()}
                </span>
              </div>
            </div>

            <h1 className="text-3xl font-bold text-white mb-4">
              {guideline.title}
            </h1>

            <p className="text-slate-300 mb-6">
              {guideline.description}
            </p>

            <div className="flex flex-wrap gap-2">
              {guideline.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-slate-700 text-slate-300 rounded-md text-sm border border-slate-600"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Paywall */}
          <div className="bg-gradient-to-br from-emerald-900/30 to-teal-900/30 rounded-xl border-2 border-emerald-600/50 p-12 text-center">
            <div className="w-20 h-20 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-lock-line text-3xl text-white"></i>
            </div>
            
            <h2 className="text-3xl font-bold text-white mb-4">
              Premium Content
            </h2>
            
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              This guideline is available exclusively to premium members. Upgrade now to access all {allGuidelines.length}+ comprehensive loan guidelines.
            </p>

            <div className="bg-slate-800/50 rounded-lg p-6 mb-8 max-w-md mx-auto">
              <h3 className="text-lg font-semibold text-white mb-4">Premium Membership Includes:</h3>
              <ul className="space-y-3 text-left">
                <li className="flex items-start text-slate-300">
                  <i className="ri-check-line text-emerald-400 text-xl mr-3 mt-0.5 flex-shrink-0"></i>
                  <span>Access to all {allGuidelines.length}+ loan guidelines</span>
                </li>
                <li className="flex items-start text-slate-300">
                  <i className="ri-check-line text-emerald-400 text-xl mr-3 mt-0.5 flex-shrink-0"></i>
                  <span>County assessor directory access</span>
                </li>
                <li className="flex items-start text-slate-300">
                  <i className="ri-check-line text-emerald-400 text-xl mr-3 mt-0.5 flex-shrink-0"></i>
                  <span>Download guidelines as PDF</span>
                </li>
                <li className="flex items-start text-slate-300">
                  <i className="ri-check-line text-emerald-400 text-xl mr-3 mt-0.5 flex-shrink-0"></i>
                  <span>Bookmark and save favorites</span>
                </li>
                <li className="flex items-start text-slate-300">
                  <i className="ri-check-line text-emerald-400 text-xl mr-3 mt-0.5 flex-shrink-0"></i>
                  <span>Priority support</span>
                </li>
                <li className="flex items-start text-slate-300">
                  <i className="ri-check-line text-emerald-400 text-xl mr-3 mt-0.5 flex-shrink-0"></i>
                  <span>14-day free trial</span>
                </li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/pricing')}
                className="bg-emerald-600 text-white px-8 py-4 rounded-lg hover:bg-emerald-500 font-semibold text-lg cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-vip-crown-line mr-2"></i>
                Start Free Trial
              </button>
              <button
                onClick={() => navigate('/pricing')}
                className="bg-slate-700 text-slate-300 px-8 py-4 rounded-lg hover:bg-slate-600 font-semibold text-lg cursor-pointer whitespace-nowrap transition-colors border border-slate-600"
              >
                View Pricing Plans
              </button>
            </div>

            <p className="text-slate-400 text-sm mt-6">
              No credit card required for trial • Cancel anytime
            </p>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  // Premium member - show full content
  const getSpecificContent = (guideline: any) => {
    return [
      {
        title: "Overview and Purpose",
        content: `${guideline.description} This guideline establishes comprehensive requirements and standards that must be followed to ensure compliance with ${guideline.source} lending policies and procedures. Understanding these requirements is essential for successful loan origination and processing.`
      },
      {
        title: "Key Requirements and Standards",
        content: `All loans must meet the specific criteria outlined in this guideline. Key areas include complete and accurate documentation requirements, borrower qualification standards and credit criteria, property eligibility and condition standards, underwriting considerations and risk assessment, compliance with federal and state regulations, and quality control and audit requirements. Lenders must ensure full compliance to maintain ${guideline.source} approval status and avoid loan delivery issues.`
      },
      {
        title: "Documentation and Verification",
        content: "Required documentation must be complete, accurate, and properly executed. All forms must be current versions from the agency and properly completed. Documentation requirements include borrower identification and verification, income and employment documentation, asset verification and source of funds, credit reports and explanations, property appraisal and inspection reports, and title and insurance documentation. Documentation should be organized and accessible for quality control reviews and audits."
      },
      {
        title: "Compliance and Best Practices",
        content: `Effective implementation requires comprehensive staff training on guideline requirements, updated procedures and workflow processes, regular monitoring and quality control reviews, staying current with guideline updates and changes, maintaining proper documentation and file organization, understanding investor overlays and requirements, and effective communication with all parties. This guideline is subject to periodic updates. Non-compliance may result in loan delivery issues, repurchase requests from ${guideline.source}, or loss of approved lender status.`
      }
    ];
  };

  const sections = getSpecificContent(guideline);

  const handleDownloadPDF = () => {
    try {
      const content = `LoanGuide Pro - ${guideline.title}

Source: ${guideline.source}
Category: ${guideline.category}
Last Updated: ${guideline.lastUpdated}

Description:
${guideline.description}

Detailed Guidelines:

${sections.map((section, index) => `${index + 1}. ${section.title}

${section.content.replace(/•/g, '\n  •')}
`).join('\n')}

Tags: ${guideline.tags.join(', ')}

---
Generated by LoanGuide Pro
Visit: https://loanguide.pro
Date Generated: ${new Date().toLocaleDateString()}`;

      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      link.href = url;
      link.download = `${guideline.title.replace(/[^a-zA-Z0-9\\s]/g, '').replace(/\\s+/g, '_')}.txt`;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 1000);
      
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-slate-400 hover:text-emerald-400 mb-8 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line mr-2"></i>
          Back to Guidelines
        </button>

        {/* Header */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-8 mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start mb-6 gap-4">
            <span className={`px-4 py-2 rounded-full text-sm font-medium border ${getAgencyColor(guideline.source)}`}>
              {guideline.source}
            </span>
            <div className="flex items-center space-x-4 text-sm text-slate-400">
              <span className="flex items-center">
                <i className="ri-folder-line mr-1"></i>
                {guideline.category}
              </span>
              <span>
                Updated {new Date(guideline.lastUpdated).toLocaleDateString()}
              </span>
            </div>
          </div>

          <h1 className="text-3xl font-bold text-white mb-4">
            {guideline.title}
          </h1>

          <div className="flex flex-wrap gap-2 mb-6">
            {guideline.tags.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-slate-700 text-slate-300 rounded-md text-sm border border-slate-600"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <div key={index} className="bg-slate-800 rounded-xl border border-slate-700 p-8">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-white text-sm font-bold">{index + 1}</span>
                </div>
                {section.title}
              </h2>
              <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">
                {section.content}
              </p>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-8 mt-8">
          <h3 className="text-lg font-semibold text-white mb-4">Need More Information?</h3>
          <div className="flex flex-col sm:flex-row gap-4">
            <button 
              onClick={handleDownloadPDF}
              className="flex-1 bg-emerald-600 text-white py-3 px-6 rounded-lg hover:bg-emerald-500 font-medium cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-download-line mr-2"></i>
              Download PDF
            </button>
            <button className="flex-1 bg-slate-700 text-slate-300 py-3 px-6 rounded-lg hover:bg-slate-600 font-medium cursor-pointer whitespace-nowrap transition-colors border border-slate-600">
              <i className="ri-bookmark-line mr-2"></i>
              Bookmark Guideline
            </button>
            <button className="flex-1 bg-slate-700 text-slate-300 py-3 px-6 rounded-lg hover:bg-slate-600 font-medium cursor-pointer whitespace-nowrap transition-colors border border-slate-600">
              <i className="ri-share-line mr-2"></i>
              Share Guideline
            </button>
          </div>
        </div>

        {/* Related Guidelines */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-8 mt-8">
          <h3 className="text-lg font-semibold text-white mb-6">Related Guidelines</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {allGuidelines
              .filter(g => g.id !== guideline.id && (g.source === guideline.source || g.category === guideline.category))
              .slice(0, 4)
              .map((relatedGuideline) => (
                <button
                  key={relatedGuideline.id}
                  onClick={() => {
                    window.scrollTo(0, 0);
                    navigate(`/guideline/${relatedGuideline.id}`);
                  }}
                  className="text-left p-4 bg-slate-700 rounded-lg border border-slate-600 hover:border-slate-500 transition-colors cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getAgencyColor(relatedGuideline.source)}`}>
                      {relatedGuideline.source}
                    </span>
                  </div>
                  <h4 className="text-white font-medium mb-2 line-clamp-2">
                    {relatedGuideline.title}
                  </h4>
                  <p className="text-slate-400 text-sm line-clamp-2">
                    {relatedGuideline.description}
                  </p>
                </button>
              ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
